/**
 * Centralised list of protected application routes.
 * Each entry: { path, Component, admin?, module? }.
 *   - admin: wraps in <AdminGuard>
 *   - module: user must have access to the given effective_module
 *             (checked by <ModuleGuard> in App.js).
 *
 * Public routes (login, public survey landing) stay inline in App.js so the
 * router tree remains self-documenting about auth boundaries.
 */

import DashboardPage from "../pages/DashboardPage.jsx";
import ServiceCasesPage from "../pages/ServiceCasesPage.jsx";
import ServiceCaseDetailPage from "../pages/ServiceCaseDetailPage.jsx";
import TasksPage from "../pages/TasksPage.jsx";
import PartsRequestsPage from "../pages/PartsRequestsPage.jsx";
import PartsRequestDetailPage from "../pages/PartsRequestDetailPage.jsx";
import CustomerFeedbackPage from "../pages/CustomerFeedbackPage.jsx";
import ReportsPage from "../pages/ReportsPage.jsx";
import AdminPage from "../pages/AdminPage.jsx";
import ClassificationRequestsPage from "../pages/ClassificationRequestsPage.jsx";
import ClassificationRequestDetailPage from "../pages/ClassificationRequestDetailPage.jsx";
import WarrantyClaimsPage from "../pages/WarrantyClaimsPage.jsx";
import WarrantyClaimDetailPage from "../pages/WarrantyClaimDetailPage.jsx";
import OnboardingReport from "../pages/OnboardingReport.jsx";
import MaintenancePlansPage from "../pages/MaintenancePlansPage.jsx";
import FlowAnalyticsPage from "../pages/FlowAnalyticsPage.jsx";
import ProfilePage from "../pages/ProfilePage.jsx";
import VehicleRegistrationsPage from "../pages/VehicleRegistrationsPage.jsx";
import VehicleRegistrationDetailPage from "../pages/VehicleRegistrationDetailPage.jsx";
import CustomersPage from "../pages/CustomersPage.jsx";
import CustomerDetailPage from "../pages/CustomerDetailPage.jsx";
import VehicleQuotesPage from "../pages/VehicleQuotesPage.jsx";
import PartsBenchOrdersPage from "../pages/PartsBenchOrdersPage.jsx";
import PartsBenchOrderDetailPage from "../pages/PartsBenchOrderDetailPage.jsx";
import BodyShopCasesPage from "../pages/BodyShopCasesPage.jsx";
import BodyShopCaseDetailPage from "../pages/BodyShopCaseDetailPage.jsx";
import OpportunitiesPage from "../pages/OpportunitiesPage.jsx";
import ComplaintsPage from "../pages/ComplaintsPage.jsx";
import CustomerProfilePage from "../pages/CustomerProfilePage.jsx";
import SatisfactionDashboardPage from "../pages/SatisfactionDashboardPage.jsx";
import SurveyAnalyticsPage from "../pages/SurveyAnalyticsPage.jsx";
import BodyShopBoardPage from "../pages/BodyShopBoardPage.jsx";
import BodyShopCalendarPage from "../pages/BodyShopCalendarPage.jsx";
import HelpPage from "../pages/HelpPage.jsx";
import BranchMapPage from "../pages/BranchMapPage.jsx";
import AnnouncementsPage from "../pages/AnnouncementsPage.jsx";
import WorkshopBoardPage from "../pages/WorkshopBoardPage.jsx";
import LogisticsPage from "../pages/LogisticsPage.jsx";
import VehiclePage from "../pages/VehiclePage.jsx";
import LogisticsSettingsPage from "../pages/LogisticsSettingsPage.jsx";
import LogisticsAlertsPage from "../pages/LogisticsAlertsPage.jsx";
import BroadcastEmailPage from "../pages/BroadcastEmailPage.jsx";

export const PROTECTED_ROUTES = [
  { path: "/", Component: DashboardPage, module: "dashboard" },
  { path: "/service-cases", Component: ServiceCasesPage, module: "service_cases" },
  { path: "/service-cases/:id", Component: ServiceCaseDetailPage, module: "service_cases" },
  { path: "/tasks", Component: TasksPage, module: "tasks" },
  { path: "/parts-requests", Component: PartsRequestsPage, module: "parts_requests" },
  { path: "/parts-requests/:id", Component: PartsRequestDetailPage, module: "parts_requests" },
  { path: "/customer-feedback", Component: CustomerFeedbackPage, module: "customer_feedback" },
  { path: "/classification-requests", Component: ClassificationRequestsPage, module: "classification_requests" },
  { path: "/classification-requests/:id", Component: ClassificationRequestDetailPage, module: "classification_requests" },
  { path: "/warranty-claims", Component: WarrantyClaimsPage, module: "warranty_claims" },
  { path: "/warranty-claims/:id", Component: WarrantyClaimDetailPage, module: "warranty_claims" },
  { path: "/reports/onboarding-24h", Component: OnboardingReport, module: "reports" },
  { path: "/maintenance-plans", Component: MaintenancePlansPage, module: "maintenance_plans" },
  { path: "/vehicle-registrations", Component: VehicleRegistrationsPage, module: "vehicle_registrations" },
  { path: "/vehicle-registrations/:id", Component: VehicleRegistrationDetailPage, module: "vehicle_registrations" },
  { path: "/customers", Component: CustomersPage, module: "crm" },
  { path: "/customers/:id", Component: CustomerDetailPage, module: "crm" },
  { path: "/vehicle-quotes", Component: VehicleQuotesPage, module: "vehicle_quotes" },
  { path: "/parts-bench-orders", Component: PartsBenchOrdersPage, module: "parts_bench_orders" },
  { path: "/parts-bench-orders/:id", Component: PartsBenchOrderDetailPage, module: "parts_bench_orders" },
  { path: "/body-shop-cases", Component: BodyShopCasesPage, module: "body_shop_cases" },
  { path: "/body-shop-cases/board", Component: BodyShopBoardPage, module: "body_shop_cases" },
  { path: "/body-shop-cases/calendar", Component: BodyShopCalendarPage, module: "body_shop_cases" },
  { path: "/body-shop-cases/:id", Component: BodyShopCaseDetailPage, module: "body_shop_cases" },
  { path: "/opportunities", Component: OpportunitiesPage, module: "crm" },
  { path: "/complaints", Component: ComplaintsPage, module: "crm" },
  { path: "/satisfaction-dashboard", Component: SatisfactionDashboardPage, module: "reports" },
  { path: "/satisfaction-analytics", Component: SurveyAnalyticsPage, module: "reports" },
  { path: "/crm/customers/:id", Component: CustomerProfilePage, module: "crm" },
  { path: "/reports", Component: ReportsPage, module: "reports" },
  { path: "/profile", Component: ProfilePage },
  { path: "/ajuda", Component: HelpPage },
  { path: "/anuncios", Component: AnnouncementsPage },
  { path: "/mapa", Component: BranchMapPage, module: "dashboard" },
  { path: "/oficina", Component: WorkshopBoardPage, module: "dashboard" },
  { path: "/logistica", Component: LogisticsPage, module: "vehicle_logistics" },
  { path: "/logistica/configuracoes", Component: LogisticsSettingsPage, admin: true, module: "vehicle_logistics" },
  { path: "/logistica/alertas", Component: LogisticsAlertsPage, module: "vehicle_logistics" },
  { path: "/vehicles/:vin", Component: VehiclePage, module: "vehicle_logistics" },
  { path: "/admin/flow-analytics", Component: FlowAnalyticsPage, admin: true, module: "reports" },
  { path: "/admin", Component: AdminPage, admin: true, module: "admin_settings" },
  { path: "/admin/broadcast", Component: BroadcastEmailPage, admin: true },
];
